import type { FormEvent } from 'react';
import React, { useState } from 'react';
import { InputField, NotificationWarning } from '@noths/polaris-client-ribbons-design-system';
import { Button } from '@noths/polaris-design-system-core-components';

import * as tracking from './tracking';
import * as styles from './NewsletterSignUpForm.styles';
import type { Environment } from 'client-site-chrome-ui/environment';
import { useCommunicationPreferences } from 'client-site-chrome-ui/hooks/useCommunicationPreferences/useCommunicationPreferences';

const PRIVACY_URL = 'https://www.notonthehighstreet.com/about/privacy';

export interface NewsletterSignUpFormProps {
  env: Environment;
}

const isValidEmailAddress = (email: string) => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
};

export const NewsletterSignUpForm = ({ env }: NewsletterSignUpFormProps) => {
  const { hasSignUpError, loading, onSignUp } = useCommunicationPreferences(env);
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState('');

  const handleChange = (evt: FormEvent<HTMLInputElement>) => setValue(evt.currentTarget.value);

  const handleSubmit = (evt: FormEvent) => {
    evt.preventDefault();
    setFieldError('');
    const email = value.trim();
    if (!email) {
      setFieldError('Email address is required');
      return;
    }
    if (!isValidEmailAddress(email)) {
      setFieldError('Email address is not valid');
      return;
    }

    onSignUp(email);
  };

  return (
    <div>
      <form css={styles.form} noValidate onSubmit={handleSubmit}>
        <h3 css={styles.heading}>Get first dibs</h3>
        <p css={styles.subHeading}>Sign up for sales, discounts and small brand inspiration</p>
        <div css={styles.formButtons}>
          <InputField
            disabled={loading}
            error={fieldError}
            id="newsletter-signup"
            label="Newsletter signup"
            labelHidden
            onChange={handleChange}
            placeholder="Enter your email address"
            type="email"
            value={value}
          />
          <Button disabled={loading}>Subscribe</Button>
        </div>
        {hasSignUpError && (
          <NotificationWarning>Subscribing failed, try again later</NotificationWarning>
        )}
        <small css={styles.privacyPolicy}>
          See our&nbsp;
          <a
            css={styles.privacyPolicy}
            href={PRIVACY_URL}
            onClick={tracking.sendPrivacyPolicySelectedEvent}
          >
            privacy policy
          </a>
          &nbsp;to understand how we process your personal data to send you marketing emails
        </small>
      </form>
    </div>
  );
};
